import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'wouter';
import { analytics } from '@/lib/analytics';

// Hook để sử dụng Google Analytics
export function useAnalytics() {
  const [location] = useLocation();
  const lastPath = useRef<string>('');

  // Auto track page views
  useEffect(() => {
    if (location !== lastPath.current) {
      analytics.pageview(location);
      lastPath.current = location;
    }
  }, [location]);

  // Track page view manually
  const trackPageView = useCallback((path: string, title?: string) => {
    analytics.pageview(path, title);
  }, []);

  // Track custom event
  const trackEvent = useCallback((
    action: string, 
    category: string, 
    label?: string, 
    value?: number
  ) => {
    analytics.event(action, category, label, value);
  }, []);

  // Track user engagement
  const trackEngagement = useCallback((
    action: string, 
    details?: Record<string, any>
  ) => {
    analytics.trackEngagement(action, details);
  }, []);

  // Track video interactions
  const trackVideo = useCallback((
    action: string, 
    videoId: string, 
    details?: Record<string, any>
  ) => {
    analytics.trackVideo(action, videoId, details);
  }, []);

  // Track payment events
  const trackPayment = useCallback((
    action: string, 
    paymentMethod: string, 
    amount?: number, 
    currency?: string
  ) => {
    analytics.trackPayment(action, paymentMethod, amount, currency);
  }, []);

  // Track form submissions
  const trackForm = useCallback((
    action: string, 
    formName: string, 
    success: boolean
  ) => {
    analytics.trackForm(action, formName, success);
  }, []);

  // Track user actions
  const trackUserAction = useCallback((
    action: string, 
    category: string, 
    details?: Record<string, any>
  ) => {
    analytics.trackUserAction(action, category, details);
  }, []);

  // Set user properties
  const setUserProperties = useCallback((properties: Record<string, any>) => {
    analytics.setUserProperties(properties);
  }, []);

  // Track conversion
  const trackConversion = useCallback((
    conversionId: string, 
    value?: number, 
    currency?: string
  ) => {
    analytics.trackConversion(conversionId, value, currency);
  }, []);

  // Get analytics status
  const getStatus = useCallback(() => {
    return analytics.getStatus();
  }, []);

  // Force load GA
  const forceLoad = useCallback(() => {
    analytics.forceLoad();
  }, []);

  return {
    trackPageView,
    trackEvent,
    trackEngagement,
    trackVideo,
    trackPayment,
    trackForm,
    trackUserAction,
    setUserProperties,
    trackConversion,
    getStatus,
    forceLoad,
  };
}

// Hook để track specific user interactions
export function useTrackInteraction() {
  const { trackEvent, trackEngagement } = useAnalytics();

  // Track button clicks
  const trackButtonClick = useCallback((
    buttonName: string, 
    location: string,
    additionalData?: Record<string, any>
  ) => {
    trackEvent('button_click', 'ui_interaction', buttonName);
    trackEngagement('button_click', {
      button_name: buttonName,
      location,
      ...additionalData,
    });
  }, [trackEvent, trackEngagement]);

  // Track form interactions
  const trackFormInteraction = useCallback((
    formName: string, 
    action: 'start' | 'complete' | 'error',
    additionalData?: Record<string, any>
  ) => {
    trackEvent('form_interaction', 'form', `${formName}_${action}`);
    trackEngagement('form_interaction', {
      form_name: formName,
      action,
      ...additionalData,
    });
  }, [trackEvent, trackEngagement]);

  // Track video interactions
  const trackVideoInteraction = useCallback((
    videoId: string,
    action: 'play' | 'pause' | 'complete' | 'seek',
    additionalData?: Record<string, any>
  ) => {
    trackEvent('video_interaction', 'video', `${action}_${videoId}`);
    trackEngagement('video_interaction', {
      video_id: videoId,
      action,
      ...additionalData,
    });
  }, [trackEvent, trackEngagement]);

  // Track payment interactions
  const trackPaymentInteraction = useCallback((
    paymentMethod: string,
    action: 'start' | 'complete' | 'error',
    amount?: number,
    additionalData?: Record<string, any>
  ) => {
    trackEvent('payment_interaction', 'payment', `${action}_${paymentMethod}`);
    trackEngagement('payment_interaction', {
      payment_method: paymentMethod,
      action,
      amount,
      ...additionalData,
    });
  }, [trackEvent, trackEngagement]);

  return {
    trackButtonClick,
    trackFormInteraction,
    trackVideoInteraction,
    trackPaymentInteraction,
  };
}
